import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { useEffect, lazy, Suspense } from "react";

// Critical pages loaded immediately
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";

// Lazy load all other pages for code splitting
const StairliftCalculator = lazy(() => import("./pages/StairliftCalculator"));
const Stairlift = lazy(() => import("./pages/Stairlift"));
const Elevator = lazy(() => import("./pages/Elevator"));
const HomeElevator = lazy(() => import("./pages/HomeElevator"));
const AboutUs = lazy(() => import("./pages/AboutUs"));
const ElevatorBenefits = lazy(() => import("./pages/ElevatorBenefits"));
const ElevatorEfficiency = lazy(() => import("./pages/ElevatorEfficiency"));
const ElevatorFAQ = lazy(() => import("./pages/ElevatorFAQ"));
const ElevatorFeatures = lazy(() => import("./pages/ElevatorFeatures"));
const ElevatorReviews = lazy(() => import("./pages/ElevatorReviews"));
const ElevatorSafety = lazy(() => import("./pages/ElevatorSafety"));
const ElevatorForYourHome = lazy(() => import("./pages/ElevatorForYourHome"));
const ElevatorGlossary = lazy(() => import("./pages/ElevatorGlossary"));
const ElevatorHowTo = lazy(() => import("./pages/ElevatorHowTo"));
const ElevatorOurTeam = lazy(() => import("./pages/ElevatorOurTeam"));
const StairliftAbout = lazy(() => import("./pages/StairliftAbout"));
const StairliftBenefits = lazy(() => import("./pages/StairliftBenefits"));
const StairliftFAQ = lazy(() => import("./pages/StairliftFAQ"));
const StairliftHowTheyWork = lazy(() => import("./pages/StairliftHowTheyWork"));
const StairliftReviews = lazy(() => import("./pages/StairliftReviews"));
const StairliftCostGuide = lazy(() => import("./pages/StairliftCostGuide"));
const ResidentialElevatorCost = lazy(() => import("./pages/ResidentialElevatorCost"));
const HomeElevatorModels = lazy(() => import("./pages/HomeElevatorModels"));
const WheelchairLift = lazy(() => import("./pages/WheelchairLift"));
const Contact = lazy(() => import("./pages/Contact"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const ThankYou = lazy(() => import("./pages/ThankYou"));
const FreeQuote = lazy(() => import("./pages/FreeQuote"));

const queryClient = new QueryClient();

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="animate-pulse text-muted-foreground">Loading...</div>
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <BrowserRouter>
        <ScrollToTop />
        <main id="main-content">
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/stair-lift-cost-calculator" element={<StairliftCalculator />} />
              <Route path="/stair-lifts" element={<Stairlift />} />
              <Route path="/stairlift" element={<Navigate to="/stair-lifts" replace />} />
              <Route path="/home-elevator-cost-calculator" element={<Elevator />} />
              <Route path="/home-elevator" element={<HomeElevator />} />
              <Route path="/elevator" element={<HomeElevator />} />
              <Route path="/about-us" element={<AboutUs />} />
              <Route path="/elevator-benefits" element={<ElevatorBenefits />} />
              <Route path="/elevator-efficiency" element={<ElevatorEfficiency />} />
              <Route path="/elevator-faq" element={<ElevatorFAQ />} />
              <Route path="/elevator-features" element={<ElevatorFeatures />} />
              <Route path="/elevator-pricing" element={<Navigate to="/residential-elevator-cost" replace />} />
              <Route path="/elevator-reviews" element={<ElevatorReviews />} />
              <Route path="/elevator-safety" element={<ElevatorSafety />} />
              <Route path="/elevator-for-your-home" element={<ElevatorForYourHome />} />
              <Route path="/for-your-home" element={<ElevatorForYourHome />} />
              <Route path="/elevator-glossary" element={<ElevatorGlossary />} />
              <Route path="/elevator-how-to" element={<ElevatorHowTo />} />
              <Route path="/elevator-our-team" element={<ElevatorOurTeam />} />
              <Route path="/our-team" element={<ElevatorOurTeam />} />
              <Route path="/about-stairlifts" element={<StairliftAbout />} />
              <Route path="/stairlift-benefits" element={<StairliftBenefits />} />
              <Route path="/stairlift-faq" element={<StairliftFAQ />} />
              <Route path="/how-stairlifts-work" element={<StairliftHowTheyWork />} />
              <Route path="/stairlift-reviews" element={<StairliftReviews />} />
              <Route path="/how-much-does-a-stairlift-cost" element={<StairliftCostGuide />} />
              <Route path="/stairlift-cost-guide" element={<StairliftCostGuide />} />
              <Route path="/residential-elevator-cost" element={<ResidentialElevatorCost />} />
              <Route path="/home-elevator-pricing" element={<ResidentialElevatorCost />} />
              <Route path="/home-elevator-cost" element={<Navigate to="/residential-elevator-cost" replace />} />
              <Route path="/home-elevator-models" element={<HomeElevatorModels />} />
              <Route path="/wheelchair-lift" element={<WheelchairLift />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms-of-service" element={<TermsOfService />} />
              <Route path="/free-quote" element={<FreeQuote />} />
              <Route path="/thank-you" element={<ThankYou />} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </main>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
